import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { displayGrossCents } from '@/utils/money';
import { DEFAULT_VAT_PERCENT } from '@/utils/vat';
import { BookingReviewNotice } from '@/components/workorders/BookingReviewNotice';
import { WorkOrderOverrunBadge } from '@/components/workorders/WorkOrderOverrunBadge';
import { WorkOrderStatusDateCell } from '@/components/workorders/WorkOrderStatusDateCell';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Alert,
  Chip,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Fab,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { getStatusIconComponent } from '@/lib/statusIconUtils';
import {
  WorkOrdersKanbanView,
  WorkOrderCreateModal,
  type ViewMode,
} from '@/components/workorders';
import {
  isWorkOrderListSortKey,
  usePagedWorkOrders,
  useWorkOrders,
  useWorkOrderStats,
  type WorkOrderListItem,
  type WorkOrderPageFilters,
} from '@/hooks/useWorkOrders';
import { useMechanics } from '@/hooks/useStaffUsers';
import { useDebounce } from '@/hooks/useDebounce';
import { useUrlSearchParam } from '@/hooks/useUrlSearchParam';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatDateTimeShort } from '@/utils/locale';
import {
  DataTable,
  type DataTableColumn,
  EntityListResults,
  LegacySourceIcon,
  RegistrationPlate,
  StatusBadge,
  VehicleMakeLogo,
} from '@/components/common';
import { useWorkOrderFlow } from '@/hooks/useWorkOrderFlow';
import { DEFAULT_FLOW_STATUS_KEYS } from '@/lib/workOrderFlowDefaults';
import {
  OTHER_STATUSES_KEY,
  WAITING_APPROVAL_STATUS,
  approvalWaitLabel,
  partitionStatusCounts,
  showsApprovalWaitMarker,
} from '@/utils/workOrderStatusChips';
import { neutralGrey } from '@/styles/theme';
import { useCollectionSync } from '@/hooks/useEntitySync';
import { queryKeys } from '@/hooks/queryKeys';
import type { BookingStatus, WorkOrder } from '@/types';
import type { WorkOrderStatus } from '@/components/common/StatusBadge';
import { useOpenWorkOrder, useViewedWorkOrderId } from '@/hooks/useWorkOrderWindow';
import { useQuoteQueueCounts } from '@/hooks/useQuoteQueue';
import { QuoteQueueTab } from '@/features/quotes/QuoteQueueTab';
import { api } from '@/lib/api';
import { BULK_WORK_ORDER_PDF_MAX } from '@/lib/bulkWorkOrderPdf';
import { WorkOrderPdfSelectionBar } from './WorkOrderPdfSelectionBar';

// Map backend status to frontend display status
const backendStatusMap: Record<string, BookingStatus> = {
  'quote': 'quote',
  'pending': 'pending',
  'confirmed': 'confirmed',
  'vehicle_arrived': 'vehicle_arrived',
  'in_progress': 'in_progress',
  'paused': 'paused',
  'waiting_parts': 'waiting_parts',
  'waiting_approval': 'waiting_approval',
  'work_completed': 'work_completed',
  'ready_to_invoice': 'ready_to_invoice',
  'on_consolidated_invoice': 'on_consolidated_invoice',
  'invoiced': 'invoiced',
  'completed': 'completed',
  'cancelled': 'cancelled',
  'quote_rejected': 'quote_rejected',
};

/**
 * Status cell for a list row. A status the portal does not know (`paid`, a
 * status added on the server later) used to be drawn as "pending", which hid
 * the row's real state (#2881); it now shows the raw key on a neutral chip.
 */
function WorkOrderStatusCell({
  status,
  isWaitingForApproval,
  openApprovalCount,
}: {
  status: string;
  isWaitingForApproval?: boolean | undefined;
  openApprovalCount?: number | undefined;
}) {
  const { t } = useTranslation('workorders');
  const known = backendStatusMap[status];
  const badge = known ? (
    <StatusBadge status={known as WorkOrderStatus} />
  ) : (
    <Tooltip title={t('unknownStatusTooltip')}>
      <Chip label={status} size="small" variant="outlined" />
    </Tooltip>
  );
  // Derived approval wait (#3027): a marker beside the status, never in place
  // of it. A row whose own status is `waiting_approval` already says so.
  if (!showsApprovalWaitMarker({ status, isWaitingForApproval })) return badge;
  return (
    <>
      {badge}
      <Chip
        icon={<PendingActionsIcon />}
        label={approvalWaitLabel(t(`status.${WAITING_APPROVAL_STATUS}`), openApprovalCount)}
        size="small"
        color="info"
        variant="outlined"
      />
    </>
  );
}

// Map API work order to frontend WorkOrder type.
// Exported for tests: the mapping is where NULL-kentät kaatoivat koko näkymän (#2452).
// `noWorkOrderNumber` is the display placeholder when bookings.work_order_number is
// missing — never derive a TM- look-alike from the id (#2640, #2907).
export function mapToWorkOrder(wo: WorkOrderListItem, noWorkOrderNumber = ''): WorkOrder {
  const nameParts = wo.customerName.trim().split(' ');
  const customerFirstName = nameParts[0] ?? '';
  const customerLastName = nameParts.slice(1).join(' ') || '';

  const makeModelParts = wo.vehicleMakeModel.trim().split(' ');
  const make = makeModelParts[0] ?? '';
  const model = makeModelParts.slice(1).join(' ') || '';

  return {
    id: wo.id,
    workOrderNumber: wo.workOrderNumber ?? noWorkOrderNumber,
    bookingId: wo.id,
    requiresStaffReview: wo.requiresStaffReview ?? false,
    customerId: wo.customerId,
    customer: {
      id: wo.customerId,
      firstName: customerFirstName,
      lastName: customerLastName,
      email: '',
      phone: wo.customerPhone,
    },
    vehicleId: wo.vehicleId,
    vehicle: {
      id: wo.vehicleId,
      customerId: wo.customerId,
      registrationNumber: wo.vehicleRegNumber,
      make,
      model,
      year: 0,
    },
    mechanicId: wo.assignedMechanicId ?? undefined,
    mechanic: wo.mechanicName ? {
      id: wo.assignedMechanicId ?? '',
      firstName: wo.mechanicName.split(' ')[0] ?? '',
      lastName: wo.mechanicName.split(' ').slice(1).join(' ') || '',
      email: '',
      phone: '',
      skills: [],
      isAvailable: true,
    } : undefined,
    // Keep the backend key as-is when it is not in the portal map. Mapping
    // unknown values to `pending` hid paid/legacy rows on the kanban (#2918).
    status: backendStatusMap[wo.status] ?? wo.status,
    rawStatus: wo.status,
    estimatedDurationHours: wo.estimatedDurationHours,
    ...(typeof wo.actualDurationHours === 'number'
      ? { actualDurationHours: wo.actualDurationHours }
      : {}),
    services: (wo.serviceType ?? '')
      .split(', ')
      .filter(Boolean)
      .map((name, i) => ({
        id: `svc-${i}`,
        name,
        description: '',
        status: 'pending' as const,
        parts: [],
        laborHours: 0,
        laborPriceCents: 0,
      })),
    // Tulosteen rivit ja rollupit ovat verollisia; veroperuste erikseen
    // (docs/VAT-BASIS.md § 5).
    totalPartsCents: displayGrossCents(wo.totalPartsCents, true, wo.vatPercent ?? DEFAULT_VAT_PERCENT),
    totalLaborCents: displayGrossCents(wo.totalLaborCents, true, wo.vatPercent ?? DEFAULT_VAT_PERCENT),
    totalCents: wo.totalWithVatCents != null && wo.taxCents != null
      ? wo.totalWithVatCents - wo.taxCents
      : wo.totalPartsCents + wo.totalLaborCents,
    vatPercent: wo.vatPercent,
    taxCents: wo.taxCents,
    totalWithVatCents: wo.totalWithVatCents,
    notes: wo.internalNotes ?? undefined,
    createdAt: wo.createdAt,
    updatedAt: wo.updatedAt,
    isWarranty: wo.isWarranty,
    isDisputed: wo.isDisputed,
    isInvoicingHold: wo.isInvoicingHold,
    // An API pod older than #3027 sends neither field.
    isWaitingForApproval: wo.isWaitingForApproval ?? false,
    openApprovalCount: wo.openApprovalCount ?? 0,
  };
}


const DEFAULT_STATUSES = ['in_progress'];

/** Work orders is the default tab and carries no ?tab param; quotes is ?tab=quotes (#3788). */
type WorkOrdersTab = 'workorders' | 'quotes';

/**
 * A date column of the table (#4128). The same short date and time as the status
 * date column; a work order without the date (no drop-off yet) shows a dash.
 */
function ListDateTimeCell({ value }: { value: string | null | undefined }) {
  if (!value) {
    return (
      <Typography variant="body2" color="text.secondary">
        -
      </Typography>
    );
  }
  return (
    <Typography variant="body2" noWrap>
      {formatDateTimeShort(value)}
    </Typography>
  );
}

export default function WorkOrdersPage() {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const openWorkOrder = useOpenWorkOrder();
  const viewedWorkOrderId = useViewedWorkOrderId();
  const isMobile = useIsMobile();
  const {
    statuses: flowStatuses,
    statusColors,
    statusIcons,
    isLoading: flowLoading,
  } = useWorkOrderFlow();

  // Real-time updates for work order list
  useCollectionSync('workorder', {
    relevantScopes: ['status', 'details', 'services', 'items', 'flags'],
    invalidationKeys: [
      { queryKey: queryKeys.workOrders.lists() },
      { queryKey: queryKeys.workOrders.stats() },
      { queryKey: queryKeys.workOrders.myServices() },
    ],
  });

  // URL-based status filter state
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: WorkOrdersTab = searchParams.get('tab') === 'quotes' ? 'quotes' : 'workorders';
  const isQuotesTab = activeTab === 'quotes';
  const { data: quoteCounts } = useQuoteQueueCounts();

  const handleTabChange = useCallback((_: React.SyntheticEvent, value: WorkOrdersTab) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'workorders') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    // The quote list's bucket and search do not belong to the work order list.
    next.delete('bucket');
    next.delete('quoteQ');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedStatuses = useMemo(() => {
    const param = searchParams.get('status');
    if (!param) {
      try {
        const stored = localStorage.getItem('vs-filter-workorders');
        if (stored && stored !== '__none__') {
          // For backward compatibility, take the first status if multiple were stored
          const parsed = stored.split(',').filter(Boolean);
          if (parsed.length > 0) return [parsed[0]];
        }
      } catch { /* ignore */ }
      return DEFAULT_STATUSES;
    }
    if (param === '__none__') return DEFAULT_STATUSES; // Fallback to default if legacy empty
    // Take only the first status (single-select)
    const parsed = param.split(',').filter(Boolean);
    return parsed.length > 0 ? [parsed[0]] : DEFAULT_STATUSES;
  }, [searchParams]);

  const setSelectedStatuses = useCallback((statuses: string[]) => {
    const next = new URLSearchParams(searchParams);
    // Single-select: always expect exactly one status
    const statusValue = (statuses.length > 0 ? statuses[0] : DEFAULT_STATUSES[0]) ?? '';
    next.set('status', statusValue);
    try { localStorage.setItem('vs-filter-workorders', statusValue); } catch { /* ignore */ }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Sync localStorage → URL on mount when no URL param
  const didSyncRef = useRef(false);
  useEffect(() => {
    if (didSyncRef.current) return;
    didSyncRef.current = true;
    if (!searchParams.has('status')) {
      try {
        const stored = localStorage.getItem('vs-filter-workorders');
        if (stored) {
          const next = new URLSearchParams(searchParams);
          next.set('status', stored);
          setSearchParams(next, { replace: true });
        }
      } catch { /* ignore */ }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search and mechanic filter live in the URL so Back restores them (#4112).
  const [searchQuery, setSearchQuery] = useUrlSearchParam('q');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [mechanicParam, setMechanicParam] = useUrlSearchParam('mechanic');
  const mechanicFilter = useMemo(
    () => mechanicParam.split(',').filter((id) => id.length > 0),
    [mechanicParam],
  );
  const setMechanicFilter = useCallback((ids: string[]) => {
    setMechanicParam(ids.join(','));
  }, [setMechanicParam]);

  // View mode (list/kanban) lives in the URL so Back from a work order
  // returns to the board (#4100). List is the default and stays out of it.
  const [viewParam, setViewParam] = useUrlSearchParam('view', { immediate: true });
  const viewMode: ViewMode = viewParam === 'kanban' ? 'kanban' : 'list';
  const setViewMode = useCallback((next: ViewMode) => {
    setViewParam(next === 'list' ? '' : next);
  }, [setViewParam]);
  const effectiveViewMode: ViewMode = isMobile ? 'list' : viewMode;

  // Stats (for toggle button counts) — same free-text and mechanic filters as
  // the list, so the chip counts describe the rows actually shown (#2154).
  // The status filter is deliberately left out: each chip counts its own status.
  const statsFilters = useMemo(
    () => ({
      q: debouncedQuery || undefined,
      mechanicId: mechanicFilter.length > 0 ? mechanicFilter.join(',') : undefined,
    }),
    [debouncedQuery, mechanicFilter],
  );
  const { data: stats } = useWorkOrderStats(statsFilters);
  const statsReady = stats !== undefined;

  // Enabled flow statuses; the default flow stands in while the flow loads.
  const enabledStatusKeys = useMemo(
    () => (flowStatuses.length === 0 ? DEFAULT_FLOW_STATUS_KEYS : flowStatuses.map((s) => s.key)),
    [flowStatuses],
  );

  // Chip counts. Rows whose status has no flow chip (legacy `confirmed`, a
  // status the tenant disabled) go to the "other" chip, so the status chips
  // always add up to the "all" chip (#2881).
  const statusCounts = useMemo(
    () => partitionStatusCounts(stats?.byStatus ?? [], enabledStatusKeys),
    [stats, enabledStatusKeys],
  );
  const otherStatuses = statusCounts.other;

  // Derived approval wait (#3027): a chip of its own unless the tenant's flow
  // already lists `waiting_approval` as a status. Its count is a flag count, so
  // it is outside the 'all' total and the 'other' chip. The list filter token
  // matches the derived flag or a legacy stored status of that name.
  const approvalWaitInFlow = enabledStatusKeys.includes(WAITING_APPROVAL_STATUS);
  // `waitingApprovalCount` is a root field of the stats, not a `byStatus` row; an
  // API pod older than #3027 sends none (undefined), while 0 is a real count.
  const approvalWaitCount = stats?.waitingApprovalCount;
  const approvalWaitSelected = (selectedStatuses[0] ?? DEFAULT_STATUSES[0]) === WAITING_APPROVAL_STATUS;
  const showApprovalWaitChip = !approvalWaitInFlow
    && ((approvalWaitCount ?? 0) > 0 || approvalWaitSelected);

  // Status toggle options — 'all', the flow statuses, the derived approval
  // wait, then 'other' when needed
  const statusToggleOptions = useMemo(
    () => [
      'all',
      ...enabledStatusKeys,
      ...(showApprovalWaitChip ? [WAITING_APPROVAL_STATUS] : []),
      ...(otherStatuses.count > 0 ? [OTHER_STATUSES_KEY] : []),
    ],
    [enabledStatusKeys, showApprovalWaitChip, otherStatuses.count],
  );

  // The status the list is filtered by. A remembered filter (URL or
  // localStorage) can name a status no chip has any more — then the list came
  // back empty with nothing highlighted (#2881). Such a filter falls back to
  // the default; one naming an "other" status selects the "other" chip.
  // Judged only once the flow and the counts have loaded.
  const rawSelection = selectedStatuses[0] ?? DEFAULT_STATUSES[0] ?? 'all';
  const activeStatus = useMemo(() => {
    // The derived token is always valid: the API answers it whatever the flow.
    if (
      rawSelection === 'all'
      || rawSelection === WAITING_APPROVAL_STATUS
      || enabledStatusKeys.includes(rawSelection)
    ) return rawSelection;
    if (flowLoading || !statsReady) return rawSelection;
    if (
      otherStatuses.count > 0
      && (rawSelection === OTHER_STATUSES_KEY || otherStatuses.statuses.includes(rawSelection))
    ) {
      return OTHER_STATUSES_KEY;
    }
    const fallback = DEFAULT_STATUSES[0] ?? 'all';
    return enabledStatusKeys.includes(fallback) ? fallback : 'all';
  }, [rawSelection, enabledStatusKeys, flowLoading, statsReady, otherStatuses]);

  // Persist the correction, but only against unfiltered counts: a search can
  // empty the "other" chip for a moment, and that must not forget the choice.
  const statsUnfiltered = !statsFilters.q && !statsFilters.mechanicId;
  useEffect(() => {
    if (activeStatus !== rawSelection && statsUnfiltered) {
      setSelectedStatuses([activeStatus]);
    }
  }, [activeStatus, rawSelection, statsUnfiltered, setSelectedStatuses]);

  // "Other" filters by the statuses behind it; until the counts load there is
  // nothing to ask for, so the list waits.
  const statusFilterValue = activeStatus === OTHER_STATUSES_KEY
    ? otherStatuses.statuses.join(',')
    : activeStatus;
  const waitingForOtherStatuses = activeStatus === OTHER_STATUSES_KEY && otherStatuses.statuses.length === 0;

  // Page size and sort live on the paged hook, so a filter change returns to page 0.
  const searchFilters = useMemo((): WorkOrderPageFilters => {
    // Kanban view shows all statuses as columns — omit status filter so every column gets data.
    // When 'all' is selected, omit status filter to show all statuses.
    const statusValue = effectiveViewMode === 'kanban' || activeStatus === 'all'
      ? undefined
      : statusFilterValue;

    const filters: WorkOrderPageFilters = {};
    if (debouncedQuery) filters.q = debouncedQuery;
    if (statusValue) filters.status = statusValue;
    if (mechanicFilter.length > 0) filters.mechanicId = mechanicFilter.join(',');
    return filters;
  }, [debouncedQuery, activeStatus, statusFilterValue, mechanicFilter, effectiveViewMode]);

  // The table pages by number. The kanban board accumulates cards instead:
  // its columns count what is on the board, so a page that replaced the
  // previous one would drop cards and miscount (#2739).
  const isKanban = effectiveViewMode === 'kanban';
  // The quotes tab has its own list; the work order queries wait while it is open.
  const paged = usePagedWorkOrders(searchFilters, { enabled: !isKanban && !waitingForOtherStatuses && !isQuotesTab });
  const board = useWorkOrders({ ...searchFilters, limit: 50 }, { enabled: isKanban && !isQuotesTab });
  const boardRows = useMemo(
    () => board.data?.pages.flatMap((page) => page.data) ?? [],
    [board.data],
  );

  const { total, sort, setSort, pagination } = paged;

  // Rows picked for the PDF download (#4031), id → label, in the order they
  // will be printed. A new filter, tab or view clears it, so rows the user no
  // longer sees are never printed by accident. Paging keeps it.
  const [pdfSelection, setPdfSelection] = useState<ReadonlyMap<string, string>>(() => new Map());
  const selectionScope = JSON.stringify([searchFilters, activeTab, effectiveViewMode]);
  useEffect(() => {
    setPdfSelection((current) => (current.size === 0 ? current : new Map()));
  }, [selectionScope]);
  const searchResults = isKanban ? boardRows : paged.rows;
  const searchLoading = isKanban ? board.isLoading : paged.isLoading;
  const searchError = isKanban ? board.isError : paged.isError;
  const searchErrorObj = isKanban ? board.error : paged.error;
  const refetchSearch = isKanban ? board.refetch : paged.refetch;

  // Mechanics for filter and assignment
  const { data: mechanicUsers = [] } = useMechanics();
  const mechanics = useMemo(() =>
    mechanicUsers.map((u) => ({
      id: u.id,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      phone: u.phone ?? '',
      skills: [],
      isAvailable: u.isActive,
    })),
    [mechanicUsers]
  );

  // Create modal state — also open via ?new=1 (work order) or ?new=quote (quotation, #560) query param
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState<'workOrder' | 'quote'>('workOrder');

  useEffect(() => {
    const newParam = searchParams.get('new');
    if (newParam === '1' || newParam === 'quote') {
      setCreateMode(newParam === 'quote' ? 'quote' : 'workOrder');
      setCreateModalOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const searchWorkOrders = useMemo(
    () => searchResults.map((wo) => mapToWorkOrder(wo, t('noWorkOrderNumber'))),
    [searchResults, t],
  );

  // Handlers
  const handleNewWorkOrder = useCallback(() => {
    setCreateMode('workOrder');
    setCreateModalOpen(true);
  }, []);

  const handleNewQuotation = useCallback(() => {
    setCreateMode('quote');
    setCreateModalOpen(true);
  }, []);

  const handleToggleStatus = useCallback((statusKey: string) => {
    // Single-select: replace current selection with new selection
    setSelectedStatuses([statusKey]);
  }, [setSelectedStatuses]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null,
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const workOrderLabel = useCallback(
    (wo: WorkOrderListItem) => wo.workOrderNumber || wo.vehicleRegNumber,
    [],
  );

  const pdfSelectionKeys = useMemo(() => new Set(pdfSelection.keys()), [pdfSelection]);

  const tableSelection = useMemo(() => {
    const rowsById = new Map(paged.rows.map((wo) => [wo.id, wo]));
    return {
      selectedKeys: pdfSelectionKeys,
      onToggleRow: (id: string) => {
        setPdfSelection((current) => {
          const next = new Map(current);
          const row = rowsById.get(id);
          if (next.has(id)) next.delete(id);
          else if (row) next.set(id, workOrderLabel(row));
          return next;
        });
      },
      onToggleAll: (ids: string[], checked: boolean) => {
        setPdfSelection((current) => {
          const next = new Map(current);
          for (const id of ids) {
            const row = rowsById.get(id);
            if (!checked) next.delete(id);
            else if (row && !next.has(id)) next.set(id, workOrderLabel(row));
          }
          return next;
        });
      },
      selectAllLabel: t('bulkPdf.selectPage'),
      getRowLabel: (id: string) => {
        const row = rowsById.get(id);
        return t('bulkPdf.selectRow', { label: row ? workOrderLabel(row) : id });
      },
    };
  }, [paged.rows, pdfSelectionKeys, workOrderLabel, t]);

  const pageFullySelected = paged.rows.length > 0
    && paged.rows.every((wo) => pdfSelection.has(wo.id));

  // "Select all N matching": one request for every row under the current
  // filters and sort, so the PDF follows the list order across pages.
  const handleSelectAllMatching = useCallback(async () => {
    const params: Record<string, string | number | undefined> = {
      ...searchFilters,
      limit: Math.min(total, BULK_WORK_ORDER_PDF_MAX),
      offset: 0,
      sortBy: sort?.sortBy ?? 'scheduledAt',
      sortDir: sort?.sortDir ?? 'desc',
    };
    const res = await api.get<{ data: WorkOrderListItem[] }>('/work-orders', { params });
    setPdfSelection(new Map(res.data.map((wo) => [wo.id, workOrderLabel(wo)])));
  }, [searchFilters, total, sort, workOrderLabel]);

  const pdfTargets = useMemo(
    () => [...pdfSelection].map(([id, label]) => ({ id, label })),
    [pdfSelection],
  );

  const handleSortChange = useCallback((sortKey: string, direction: 'asc' | 'desc') => {
    // Only booking columns the server orders by (joined columns stay unsorted).
    if (!isWorkOrderListSortKey(sortKey)) return;
    setSort({ sortBy: sortKey, sortDir: direction });
  }, [setSort]);

  // Table columns for search results
  const columns: DataTableColumn<WorkOrderListItem>[] = [
    {
      // First column: it carries the row link, so the number is the link's name (#4128).
      header: t('workOrderNumber'),
      sortKey: 'workOrderNumber',
      renderCell: (wo) => (
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          color={wo.workOrderNumber ? 'text.primary' : 'text.secondary'}
        >
          {wo.workOrderNumber || t('noWorkOrderNumber')}
        </Typography>
      ),
    },
    {
      header: t('vehicle'),
      renderCell: (wo) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VehicleMakeLogo make={wo.vehicleMakeModel.split(' ')[0]} size={32} />
          <Box>
            <RegistrationPlate registrationNumber={wo.vehicleRegNumber} />
          </Box>
        </Box>
      ),
    },
    {
      header: t('customer'),
      renderCell: (wo) => (
        <>
          <Typography variant="body2" fontWeight={500}>
            {wo.customerName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {wo.vehicleMakeModel}
          </Typography>
        </>
      ),
    },
    {
      header: t('services'),
      sortKey: 'serviceType',
      renderCell: (wo) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
          {wo.serviceType}
        </Typography>
      ),
    },
    {
      header: t('statusLabel'),
      sortKey: 'status',
      renderCell: (wo) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <WorkOrderStatusCell
            status={wo.status}
            isWaitingForApproval={wo.isWaitingForApproval}
            openApprovalCount={wo.openApprovalCount}
          />
          <BookingReviewNotice required={wo.requiresStaffReview} compact />
          <WorkOrderOverrunBadge
            plannedHours={wo.estimatedDurationHours}
            actualDurationHours={wo.actualDurationHours}
          />
        </Box>
      ),
    },
    {
      header: t('listColumns.scheduledAt'),
      sortKey: 'scheduledAt',
      renderCell: (wo) => <ListDateTimeCell value={wo.scheduledAt} />,
    },
    {
      header: t('listColumns.vehicleDropoffAt'),
      renderCell: (wo) => <ListDateTimeCell value={wo.vehicleDropoffAt} />,
    },
    {
      header: t('statusDate.column'),
      sortKey: 'statusDate',
      renderCell: (wo) => <WorkOrderStatusDateCell status={wo.status} dates={wo} />,
    },
    {
      header: t('mechanic'),
      renderCell: (wo) => {
        // Collect unique mechanics from services
        const serviceMechanics = wo.services
          .filter((s) => s.mechanicName)
          .map((s) => ({
            bookingServiceId: s.bookingServiceId,
            serviceName: s.name,
            mechanicId: s.assignedMechanicId,
            mechanicName: s.mechanicName,
          }));

        // Deduplicate by mechanicId
        const uniqueNames = [...new Set(serviceMechanics.map((m) => m.mechanicName))];

        // Fallback to top-level mechanic
        if (uniqueNames.length === 0 && wo.mechanicName) {
          uniqueNames.push(wo.mechanicName);
        }

        if (uniqueNames.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {uniqueNames.map((name) => (
              <Chip
                key={name}
                label={name}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      header: '',
      width: 40,
      align: 'right',
      renderCell: (wo) => <LegacySourceIcon source={wo.legacySource} />,
    },
  ];

  const renderMobileCard = (wo: WorkOrderListItem) => (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, rowGap: 0.75, mb: 1 }}>
        <RegistrationPlate registrationNumber={wo.vehicleRegNumber} />
        <WorkOrderStatusCell
            status={wo.status}
            isWaitingForApproval={wo.isWaitingForApproval}
            openApprovalCount={wo.openApprovalCount}
          />
        <BookingReviewNotice required={wo.requiresStaffReview} compact />
        <WorkOrderOverrunBadge
          plannedHours={wo.estimatedDurationHours}
          actualDurationHours={wo.actualDurationHours}
        />
        <Box sx={{ ml: 'auto' }}>
          <LegacySourceIcon source={wo.legacySource} />
        </Box>
      </Box>
      <Typography variant="body2" fontWeight={500}>
        {wo.customerName}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {wo.serviceType}
      </Typography>
      <WorkOrderStatusDateCell status={wo.status} dates={wo} compact />
    </Box>
  );

  return (
    <Box>
      {/* Header: title + add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RequestQuoteIcon />}
              onClick={handleNewQuotation}
            >
              {t('newQuotation')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewWorkOrder}
            >
              {t('newWorkOrder')}
            </Button>
          </Box>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="workorders" label={t('tabs.workorders')} />
        <Tab
          value="quotes"
          label={quoteCounts && quoteCounts.open > 0
            ? t('tabs.quotesWithCount', { count: quoteCounts.open })
            : t('tabs.quotes')}
        />
      </Tabs>

      {isQuotesTab ? (
        <QuoteQueueTab />
      ) : (
        <>
          {/* Search bar + view mode toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TextField
              size="small"
              placeholder={t('searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { xs: '100%', md: 500 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <Tooltip title={t('clearSearch')}>
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery('')}
                          aria-label={t('clearSearch')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />

            <Box sx={{ flex: 1 }} />

            {/* View Mode Toggle — hidden on mobile */}
            {!isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
              >
                <ToggleButton value="list" aria-label={t('listView')}>
                  <ViewListIcon />
                </ToggleButton>
                <ToggleButton value="kanban" aria-label={t('kanbanView')}>
                  <ViewKanbanIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {/* Status toggles + mechanic filter — hidden in kanban mode */}
          {effectiveViewMode !== 'kanban' && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 3,
                flexWrap: 'wrap',
              }}
            >
              {statusToggleOptions.map((statusKey) => {
                const isSelected = activeStatus === statusKey;
                const isOther = statusKey === OTHER_STATUSES_KEY;

                // 'all' takes the API's total. The status chips add up to it: the
                // `waiting_parts` flag count is left out (#2154) and statuses
                // without a chip of their own are counted under 'other' (#2881).
                // The derived `waiting_approval` chip shows its flag count, which
                // is likewise outside that sum (#3027).
                const count = statusKey === 'all'
                  ? (stats?.totalCount ?? 0)
                  : isOther
                    ? otherStatuses.count
                    : statusKey === WAITING_APPROVAL_STATUS
                      ? (approvalWaitCount ?? statusCounts.listed[statusKey] ?? 0)
                      : (statusCounts.listed[statusKey] ?? 0);

                // 'all' and 'other' use a neutral color; otherwise use status color
                const color = statusKey === 'all' || isOther
                  ? neutralGrey
                  : (statusColors[statusKey] ?? neutralGrey);
                const IconComp = statusKey === WAITING_APPROVAL_STATUS && !approvalWaitInFlow
                  ? PendingActionsIcon
                  : getStatusIconComponent(statusKey, statusIcons);

                // 'other' names the statuses behind it; an unknown one shows its raw key
                const tooltipLabel = statusKey === 'all'
                  ? t('filters.allStatuses')
                  : isOther
                    ? t('filters.otherStatusesTooltip', {
                        statuses: otherStatuses.statuses
                          .map((status) => t(`status.${status}`, { defaultValue: status }))
                          .join(', '),
                      })
                    : t(`status.${statusKey}`);

                return (
                  <Tooltip key={statusKey} title={tooltipLabel}>
                    <Chip
                      icon={<IconComp sx={{ fontSize: 16 }} />}
                      label={count}
                      size="small"
                      onClick={() => handleToggleStatus(statusKey)}
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        minWidth: 52,
                        ...(isSelected
                          ? {
                              bgcolor: color,
                              color: 'common.white',
                              '& .MuiChip-icon': { color: 'common.white' },
                              '&:hover': { bgcolor: color, opacity: 0.85 },
                            }
                          : {
                              bgcolor: 'transparent',
                              border: '1px solid',
                              borderColor: 'divider',
                              color: 'text.secondary',
                              '& .MuiChip-icon': { color },
                              '&:hover': { bgcolor: 'action.hover' },
                            }),
                      }}
                    />
                  </Tooltip>
                );
              })}

              <Box sx={{ flex: 1 }} />

              {/* Mechanic Filter */}
              <Autocomplete
                multiple
                size="small"
                options={mechanics}
                getOptionLabel={(m) => `${m.firstName} ${m.lastName}`}
                value={mechanics.filter((m) => mechanicFilter.includes(m.id))}
                onChange={(_, vals) => setMechanicFilter(vals.map((v) => v.id))}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('mechanic')} />
                )}
                sx={{ minWidth: { xs: 160, md: 250 } }}
              />
            </Box>
          )}

          {/* Content */}
          <Box>
            {searchError ? (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => void refetchSearch()}
                  >
                    {tc('errors.refresh')}
                  </Button>
                }
              >
                {t('errorLoading')}:{' '}
                {searchErrorObj instanceof Error
                  ? searchErrorObj.message
                  : tc('unknownError')}
              </Alert>
            ) : (
              <EntityListResults
                loading={searchLoading}
                resultCountText={isKanban ? '' : t('searchResultCount', { count: total })}
                {...(isKanban
                  ? {
                      hasNextPage: board.hasNextPage,
                      isFetchingNextPage: board.isFetchingNextPage,
                      onLoadMore: () => { void board.fetchNextPage(); },
                    }
                  : { pagination })}
              >
                {effectiveViewMode === 'kanban' ? (
                  <WorkOrdersKanbanView workOrders={searchWorkOrders} />
                ) : (
                  <>
                  {!isMobile && pdfTargets.length > 0 && (
                    <WorkOrderPdfSelectionBar
                      selected={pdfTargets}
                      matchingTotal={total}
                      pageFullySelected={pageFullySelected}
                      onSelectAllMatching={handleSelectAllMatching}
                      onClear={() => setPdfSelection(new Map())}
                    />
                  )}
                  <DataTable<WorkOrderListItem>
                    columns={columns}
                    data={searchResults}
                    getRowKey={(wo) => wo.id}
                    sortKey={sort?.sortBy ?? 'scheduledAt'}
                    sortDirection={sort?.sortDir ?? 'desc'}
                    onSortChange={handleSortChange}
                    emptyMessage={t('noWorkOrders')}
                    emptyDescription={t('noWorkOrdersDesc')}
                    loading={searchLoading}
                    renderMobileCard={renderMobileCard}
                    onRowClick={(wo, event) => { openWorkOrder(wo.id, event); }}
                    getRowHref={(wo) => `/workorders/${wo.id}`}
                    selectedRowKey={viewedWorkOrderId}
                    {...(isMobile ? {} : { selection: tableSelection })}
                  />
                  </>
                )}
              </EntityListResults>
            )}
          </Box>
        </>
      )}

      {/* Mobile FABs — primary new work order + secondary new quotation (#560) */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            right: 16,
            zIndex: 1050,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            alignItems: 'flex-end',
          }}
        >
          <Fab
            color="secondary"
            size="small"
            onClick={handleNewQuotation}
            aria-label={t('newQuotation')}
          >
            <RequestQuoteIcon />
          </Fab>
          <Fab
            color="primary"
            size="medium"
            onClick={handleNewWorkOrder}
            aria-label={t('newWorkOrder')}
          >
            <AddIcon />
          </Fab>
        </Box>
      )}

      {/* Create Work Order / Quotation Modal */}
      <WorkOrderCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        mechanics={mechanics}
        mode={createMode}
      />
    </Box>
  );
}
